import React, { useEffect, lazy, Suspense } from "react";
import "./App.css";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import Landing from "./pages/Landing"; // Eager: LCP page
import AdSense from "./components/AdSense";
import SEOHead from "./components/SEOHead";
import { Toaster } from "./components/ui/sonner";
import SupportBadge from "./components/SupportBadge";

// Everything else is code-split so the landing page bundle stays lean.
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Studio = lazy(() => import("./pages/Studio"));
const Admin = lazy(() => import("./pages/Admin"));
const Partner = lazy(() => import("./pages/Partner"));
const Premium = lazy(() => import("./pages/Premium"));
const Policy = lazy(() => import("./pages/Policy"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const MyInvoices = lazy(() => import("./pages/MyInvoices"));
const PaymentReturn = lazy(() => import("./pages/PaymentReturn"));
const CreatorOSLayout = lazy(() => import("./pages/CreatorOS/CreatorOSShell"));
const CreatorOSDashboard = lazy(() => import("./pages/CreatorOS/Dashboard"));
const CreatorOSSettings = lazy(() => import("./pages/CreatorOS/Settings"));
const CreatorOSChat = lazy(() => import("./pages/CreatorOS/Chat"));
const CreatorOSVideoDoctor = lazy(() => import("./pages/CreatorOS/VideoDoctor"));
const CreatorOSTrends = lazy(() => import("./pages/CreatorOS/Trends"));
const CreatorOSCompetitors = lazy(() => import("./pages/CreatorOS/Competitors"));
const CreatorOSAIStudio = lazy(() => import("./pages/CreatorOS/AIStudio"));
const CreatorOSThumbnailLab = lazy(() => import("./pages/CreatorOS/ThumbnailLab"));
const CreatorOSScriptLab = lazy(() => import("./pages/CreatorOS/ScriptLab"));
const CreatorOSCommentIntel = lazy(() => import("./pages/CreatorOS/CommentIntel"));
const CreatorOSPlanner = lazy(() => import("./pages/CreatorOS/Planner"));
const CreatorOSAgents = lazy(() => import("./pages/CreatorOS/Agents"));
const CreatorOSResearch = lazy(() => import("./pages/CreatorOS/Research"));
const BlogList = lazy(() => import("./pages/Blog/BlogList"));
const BlogPost = lazy(() => import("./pages/Blog/BlogPost"));
const AuthorPage = lazy(() => import("./pages/Blog/AuthorPage"));

function RouteFallback() {
  return (
    <div
      data-testid="route-fallback"
      className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-500 text-sm font-mono"
    >
      Loading…
    </div>
  );
}

function App() {
  useEffect(() => {
    // Force dark mode
    document.documentElement.classList.add("dark");
  }, []);

  return (
    <div className="App min-h-screen">
      <AuthProvider>
        <BrowserRouter>
          <SEOHead />
          <AdSense />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/studio" element={<Studio />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/partner" element={<Partner />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/invoices" element={<MyInvoices />} />
              <Route path="/payment/return" element={<PaymentReturn />} />
              <Route path="/premium" element={<Premium />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/author/:slug" element={<AuthorPage />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/creator-os" element={<CreatorOSLayout />}>
                <Route index element={<CreatorOSDashboard />} />
                <Route path="planner" element={<CreatorOSPlanner />} />
                <Route path="video-doctor" element={<CreatorOSVideoDoctor />} />
                <Route path="thumbnail-lab" element={<CreatorOSThumbnailLab />} />
                <Route path="script-lab" element={<CreatorOSScriptLab />} />
                <Route path="comments" element={<CreatorOSCommentIntel />} />
                <Route path="trends" element={<CreatorOSTrends />} />
                <Route path="competitors" element={<CreatorOSCompetitors />} />
                <Route path="ai-studio" element={<CreatorOSAIStudio />} />
                <Route path="agents" element={<CreatorOSAgents />} />
                <Route path="research" element={<CreatorOSResearch />} />
                <Route path="settings" element={<CreatorOSSettings />} />
                <Route path="chat" element={<CreatorOSChat />} />
              </Route>
              <Route path="/policy" element={<Navigate to="/policy/disclaimer" replace />} />
              <Route path="/policy/:slug" element={<Policy />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <SupportBadge />
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-left" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
